import React, { useEffect, useState } from 'react';
import { adminAPI } from '../services/api';

const ROLE_OPTIONS = [
  'SUPER_ADMIN',
  'VERIFICATION_ADMIN',
  'SUPPORT_ADMIN',
  'CONTENT_MODERATOR',
  'REPORTING_ADMIN',
  'COMPLIANCE_AUDITOR',
  'ANALYTICS_ADMIN',
];

function AdminManagement() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [resettingId, setResettingId] = useState('');

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    adminRole: 'SUPPORT_ADMIN',
  });

  const loadAdmins = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await adminAPI.getAdmins();
      setAdmins(res.data.admins || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      await adminAPI.createAdmin(form);
      setForm({
        name: '',
        email: '',
        password: '',
        adminRole: 'SUPPORT_ADMIN',
      });
      await loadAdmins();
      alert('Admin created');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create admin');
    } finally {
      setCreating(false);
    }
  };

  const onRoleChange = async (adminId, role) => {
    try {
      await adminAPI.updateAdminRole(adminId, { adminRole: role });
      await loadAdmins();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update role');
    }
  };

  const onResetPassword = async (adminId) => {
    const password = window.prompt('Enter new password (min 8 chars):');
    if (!password) return;
    try {
      setResettingId(adminId);
      await adminAPI.resetAdminPassword(adminId, { password });
      alert('Password reset');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setResettingId('');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-gray-900">Admin Management</h1>
        <p className="text-gray-600 mt-1 text-sm">
          SUPER_ADMIN only. Create admins, assign roles, and reset passwords.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Create Admin</h2>
        <form onSubmit={onCreate} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <input
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="Name"
            required
            className="px-3 py-2 border-2 border-gray-200 rounded-lg"
          />
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            placeholder="Email"
            required
            className="px-3 py-2 border-2 border-gray-200 rounded-lg"
          />
          <input
            type="password"
            value={form.password}
            onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            placeholder="Password"
            required
            className="px-3 py-2 border-2 border-gray-200 rounded-lg"
          />
          <select
            value={form.adminRole}
            onChange={(e) => setForm((p) => ({ ...p, adminRole: e.target.value }))}
            className="px-3 py-2 border-2 border-gray-200 rounded-lg"
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={creating}
            className="px-4 py-2 bg-pink-500 text-white rounded-lg font-semibold hover:bg-pink-600 disabled:opacity-50"
          >
            {creating ? 'Creating...' : 'Create Admin'}
          </button>
        </form>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Role</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Created</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a._id} className="border-t">
                  <td className="px-4 py-3">{a.name}</td>
                  <td className="px-4 py-3 text-sm">{a.email}</td>
                  <td className="px-4 py-3">
                    <select
                      value={a.adminRole}
                      onChange={(e) => onRoleChange(a._id, e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-sm"
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {a.createdAt ? new Date(a.createdAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onResetPassword(a._id)}
                      disabled={resettingId === a._id}
                      className="px-3 py-1 bg-amber-100 text-amber-700 rounded-lg text-sm font-semibold hover:bg-amber-200 disabled:opacity-50"
                    >
                      {resettingId === a._id ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminManagement;

